import React from 'react';
import axios from 'axios';
import { handleAxiosError } from 'utilities/apiUtils';
import Skeleton from 'react-loading-skeleton';
import { Paginator } from 'primereact/paginator';
import '../../styles/pages.css';
import ErrorState from 'components/ErrorState';
import { useSelectedClient } from '../../hooks/useSelectedClient';

const PAGE_SIZE = 5;

export default function RecentActivityPanel() {
  const [activity, setActivity] = React.useState<any[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [pageFirst, setPageFirst] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { insuredId } = useSelectedClient();

  React.useEffect(() => {
    setPageFirst(0);
  }, [insuredId]);

  React.useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('igtoken');
        if (!token) return;
        const params = new URLSearchParams({ hours: '0', skip: String(pageFirst), top: String(PAGE_SIZE) });
        if (insuredId) params.set('insuredDatabaseId', insuredId);
        const res = await axios.get(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/recent-activity?${params}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        const data = res.data;
        setActivity(Array.isArray(data.value) ? data.value : []);
        setTotalCount(data.totalCount ?? 0);
      } catch (err: any) {
        handleAxiosError(err);
        setError('Failed to load recent activity.');
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [insuredId, pageFirst]);

  return (
    <div className="recentActivityOuter" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px' }}>
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '11px 16px', boxShadow: '0 1px 4px rgba(44,83,100,0.07)', border: '1px solid #eaecf4', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                {/* Left: title / description / timestamp */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Skeleton width="40%" height={12} />
                  <Skeleton width="70%" height={11} />
                  <Skeleton width="25%" height={10} />
                </div>
                {/* Right: avatar + name + email */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0, borderLeft: '1px solid #eaecf4', paddingLeft: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Skeleton circle width={22} height={22} />
                    <Skeleton width={100} height={11} />
                  </div>
                  <Skeleton width={130} height={10} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <ErrorState compact />
        ) : activity.length === 0 ? (
          <div className="panel__emptyState">
            <div className="panel__emptyState__iconWrap">
              <i className="pi pi-clock panel__emptyState__icon" />
            </div>
            <div className="panel__emptyState__title">No activity to display.</div>

          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px' }}>
            {activity.map((a) => {
              let dateTimeStr = '';
              if (a.createdAt) {
                const dt = new Date(a.createdAt);
                if (!isNaN(dt.getTime())) {
                  dateTimeStr = dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) + ' ' + dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                }
              }
              const initials = a.performedByFullName
                ? a.performedByFullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
                : '?';
              return (
                <div
                  key={a.id}
                  style={{
                    background: '#fff',
                    borderRadius: 10,
                    padding: '11px 16px',
                    boxShadow: '0 1px 4px rgba(44,83,100,0.07)',
                    border: '1px solid #eaecf4',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 14,
                  }}
                >
                  {/* Left: activity info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12.5, color: '#1a56db', marginBottom: 2 }}>{a.title}</div>
                    <div style={{ fontSize: 11.5, color: '#5a6a7e', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>
                      {a.description?.replace(/certificate [a-f0-9\-]+/i, 'Certificate')}
                    </div>
                    {dateTimeStr && (
                      <span style={{ fontSize: 10, color: '#aab4c4', fontWeight: 500, letterSpacing: 0.2 }}>
                        {dateTimeStr}
                      </span>
                    )}
                  </div>

                  {/* Right: performed by */}
                  {(a.performedByFullName || a.performedByEmail) && (
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                      gap: 3, flexShrink: 0,
                      borderLeft: '1px solid #eaecf4', paddingLeft: 14,
                    }}>
                      {/* Avatar + name row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #381574 0%, #A200FF 100%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                          flexShrink: 0,
                        }}>
                          {initials}
                        </div>
                        {a.performedByFullName && (
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: '#2d3a4a', whiteSpace: 'nowrap' }}>
                            {a.performedByFullName}
                          </span>
                        )}
                      </div>
                      {/* Email */}
                      {a.performedByEmail && (
                        <span style={{ fontSize: 10, color: '#9aa5b4', whiteSpace: 'nowrap' }}>
                          {a.performedByEmail}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {totalCount > PAGE_SIZE && (
        <div style={{ borderTop: '1px solid #eaecf4', marginTop: 8, paddingTop: 10, paddingBottom: 4, display: 'flex', justifyContent: 'center' }}>
          <Paginator
            first={pageFirst}
            rows={PAGE_SIZE}
            totalRecords={totalCount}
            onPageChange={(e: { first: number }) => setPageFirst(e.first)}
            template="PrevPageLink PageLinks NextPageLink"
            className="requests-paginator"
          />
        </div>
      )}
    </div>
  );
}
